"use client";

import React from "react";
import { MapPin, Phone, Mail, Loader, MessageCircle, ShieldCheck, Star, Award } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Link } from "@/components/common/Link";
const trustBadges = [{
  icon: Award,
  text: "RGI Registered"
}, {
  icon: Star,
  text: "SEAI Registered Installer"
}, {
  icon: ShieldCheck,
  text: "Fully Insured"
}];
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="bg-card p-8 border border-border rounded-lg shadow-lg">
        <h3 className="font-default text-2xl font-bold text-foreground mb-4">
          Quote Request Sent!
        </h3>
        <p className="font-sans text-muted-foreground mb-6">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-md font-default font-semibold uppercase px-8 py-4 h-auto border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          Request Another Quote
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="name" rules={{
      required: "Name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans text-foreground font-medium">Full Name</FormLabel>
            <FormControl>
              <Input placeholder="Enter your name" className="rounded-md border-input focus-visible:ring-2 focus-visible:ring-ring bg-background" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" rules={{
        required: "Telephone is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans text-foreground font-medium">Telephone</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="085 706 3869" className="rounded-md border-input focus-visible:ring-2 focus-visible:ring-ring bg-background" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans text-foreground font-medium">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="Email Address" className="rounded-md border-input focus-visible:ring-2 focus-visible:ring-ring bg-background" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="location" rules={{
        required: "Eircode or area is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans text-foreground font-medium">Eircode or Area</FormLabel>
              <FormControl>
                <Input placeholder="e.g. Dublin 4 / D04 X2Y1" className="rounded-md border-input focus-visible:ring-2 focus-visible:ring-ring bg-background" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="service" rules={{
        required: "Required service is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans text-foreground font-medium">Required Service</FormLabel>
              <FormControl>
                <select className="rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-ring text-foreground w-full shadow-xs transition-colors outline-none" {...field}>
                  <option value="" disabled>Select a service...</option>
                  <option value="Boiler Servicing">Boiler Servicing</option>
                  <option value="Plumbing Repairs">Plumbing Repairs</option>
                  <option value="Heating Installation">Heating Installation</option>
                  <option value="Boiler Installation">Boiler Installation</option>
                  <option value="Boiler Repair">Boiler Repair</option>
                  <option value="Heat Pump Installation">Heat Pump Installation</option>
                  <option value="Emergency Plumbing">Emergency Plumbing</option>
                  <option value="Other">Other</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Details are required",
      minLength: {
        value: 10,
        message: "Please provide a bit more detail"
      },
      maxLength: {
        value: 1000,
        message: "Message is too long"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans text-foreground font-medium">Project Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your plumbing or heating needs..." className="rounded-md border-input focus-visible:ring-2 focus-visible:ring-ring bg-background min-h-[120px] resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="privacyConsent" rules={{
      required: "Privacy consent is required"
    }} render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md p-2">
            <FormControl>
              <input type="checkbox" checked={field.value || false} onChange={e => field.onChange(e.target.checked)} className="h-4 w-4 rounded border-input text-primary focus:ring-ring focus:ring-2 bg-background mt-1" />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="font-sans text-xs text-muted-foreground font-normal cursor-pointer">
                I consent to having my details stored and processed to respond to my quote request.
              </FormLabel>
              <FormMessage />
            </div>
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive text-sm font-sans rounded-md">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto bg-primary hover:bg-primary/90 text-primary-foreground rounded-md font-default font-bold uppercase px-10 py-6 h-auto text-base shadow-lg transition-all hover:scale-[1.02]">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing...
          </> : "Request a Quote"}
      </Button>
    </Form>;
}
export default function GetYourFreeQuoteToday() {
  return <section data-section-id="224961" id="get-your-free-quote-today" className="dark bg-background py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          <div className="flex flex-col justify-center">
            <h2 className="font-default text-4xl md:text-6xl font-extrabold text-foreground mb-6 tracking-tighter">
              Request a Plumbing or Heating Quote
            </h2>
            <p className="font-sans text-xl text-muted-foreground mb-12 max-w-md leading-relaxed">
              Tell us what service you need and where the property is located. For urgent plumbing problems, call directly to confirm the soonest available attendance.
            </p>

            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-card flex items-center justify-center shrink-0 border border-border shadow-sm">
                  <MapPin className="size-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-default text-lg font-bold text-foreground mb-1">
                    Service Area
                  </h3>
                  <p className="font-sans text-muted-foreground leading-relaxed">
                    Dublin and Dublin 4
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-card flex items-center justify-center shrink-0 border border-border shadow-sm">
                  <Phone className="size-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-default text-lg font-bold text-foreground mb-1">
                    Call Us Directly
                  </h3>
                  <div className="flex flex-col gap-1">
                    <p className="text-sm text-muted-foreground font-sans">
                      Office:{" "}
                      <a href="tel:+35319081819" className="font-default text-xl font-extrabold text-primary hover:text-primary/80 transition-colors tracking-tighter">
                        01 908 1819
                      </a>
                    </p>
                    <p className="text-sm text-muted-foreground font-sans">
                      Mobile:{" "}
                      <a href="tel:+353857063869" className="font-default text-xl font-extrabold text-primary hover:text-primary/80 transition-colors tracking-tighter">
                        085 706 3869
                      </a>
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-card flex items-center justify-center shrink-0 border border-border shadow-sm">
                  <Mail className="size-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-default text-lg font-bold text-foreground mb-1">
                    Email Support
                  </h3>
                  <a href="mailto:info@d4gas.ie" className="font-sans text-muted-foreground hover:text-primary transition-colors">
                    info@d4gas.ie
                  </a>
                </div>
              </div>

              <div className="pt-4">
                <Link to="https://wa.me/353857063869" newTab>
                  <Button className="bg-[#25D366] hover:bg-[#25D366]/90 text-white rounded-md font-default font-bold uppercase px-8 py-6 h-auto text-base shadow-lg transition-all hover:scale-[1.02] flex items-center gap-3">
                    <MessageCircle className="size-6 fill-current" />
                    WhatsApp Us: 085 706 3869
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          <div className="bg-card p-8 md:p-10 shadow-2xl border border-border rounded-2xl backdrop-blur-sm">
            <h3 className="font-default text-2xl font-bold text-foreground mb-8">
              Request Your Free Quote
            </h3>
            <FormProvider formKey="free_quote_form_home" formId="free_quote_form_home" sectionName="get-your-free-quote-today" defaultValues={{
            name: "",
            phone: "",
            email: "",
            location: "",
            service: "",
            message: "",
            privacyConsent: false
          }} validationRules={{
            name: {
              required: true
            },
            phone: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            location: {
              required: true
            },
            service: {
              required: true
            },
            message: {
              required: true,
              minLength: 10,
              maxLength: 1000
            },
            privacyConsent: {
              required: true
            }
          }} submitText="Request a Quote" successMessage="Thank you for reaching out! One of our engineers will contact you shortly with your free quote.">
              <ContactForm />
            </FormProvider>
          </div>
        </div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 border-t border-border pt-16">
          {trustBadges.map((badge, i) => <div key={i} data-index={i} className="flex items-center gap-4 group">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 transition-colors group-hover:bg-primary/20">
                <badge.icon className="size-6 text-primary" />
              </div>
              <span className="font-default font-bold text-foreground text-lg leading-tight">
                {badge.text}
              </span>
            </div>)}
        </div>
      </div>
    </section>;
}